import axios, { AxiosRequestConfig } from "axios";
import { store } from "src/lib/store/store";
import { refreshAccessToken } from "src/lib/auth/refreshManager";

/* Axios does not model our retry bookkeeping, so widen the config locally. */
type RetriableConfig = AxiosRequestConfig & { _retried?: boolean };

const headers = {};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_API_URL + "/api/v1",
  timeout: 20000,
  headers,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = store.getState()?.auth?.accessToken ?? "";

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config as RetriableConfig | undefined;

    /* No response object means the request never reached the server -- DNS,
       timeout, offline, CORS. Logging someone out because their wifi blinked
       would be its own bug, so these pass straight through. */
    if (!error?.response || !original) {
      return Promise.reject(error);
    }

    if (error.response.status !== 401) {
      return Promise.reject(error);
    }

    /* One attempt per request. Without this, a refresh that succeeds against
       a backend still rejecting the new token would retry forever. */
    if (original._retried) {
      return Promise.reject(error);
    }
    original._retried = true;

    /* Concurrent 401s collapse into a single refresh; the rest await it. */
    const result = await refreshAccessToken();

    /* Session is over (the modal is already up) or the refresh itself failed
       on the network. Either way this request cannot be replayed. */
    if (!result) {
      return Promise.reject(error);
    }

    original.headers = {
      ...(original.headers ?? {}),
      Authorization: `Bearer ${result.accessToken}`,
    };

    return axiosInstance(original);
  }
);

export default axiosInstance;
